import { Button, InputNumber, Modal as ModalAntd, notification, Radio } from "antd";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { BACKEND_URL } from "../../../config/config";
import { useAuth } from "../../../hooks";

import "./BuyCreditsModal.css";

export function BuyCreditsModal({ account, open, onClose, onRequested }) {
  const { user } = useAuth();
  const [amount, setAmount] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("CHECK");
  const [cardFeePercent, setCardFeePercent] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;

    axios
      .get(`${BACKEND_URL}/customers/getCardFeeRate`)
      .then((response) => setCardFeePercent(Number(response?.data?.percent) || 0))
      .catch(() => setCardFeePercent(0));
  }, [open]);

  const closeModal = () => {
    if (isSubmitting) return;
    setAmount(null);
    setPaymentMethod("CHECK");
    onClose();
  };

  // Mirrors the backend's exact CC_FEE formula (Math.round(subtotal * fee / 100),
  // both in whole dollars) so this preview matches what the generated invoice
  // will actually charge.
  const cardFeeAmount =
    paymentMethod === "CARD" && amount > 0
      ? Math.round((amount * cardFeePercent) / 100)
      : 0;

  const submitRequest = async () => {
    if (!amount || amount <= 0) {
      notification.error({ message: "Please enter an amount greater than $0." });
      return;
    }

    setIsSubmitting(true);

    const dataSend = {
      contact:
        account?.mainContact?.fullName || account?.mainContact || user?.fullName,
      accountName: account?.accountName || account?.companyName,
      account_id: account?._id,
      owner: account?.owner,
      origin: account?.companyName ? "lead" : "account",
      contact_id: user?._id,
      requestedAmount: amount,
      paymentMethod,
    };

    const response = await axios
      .post(`${BACKEND_URL}/customers/requestPaymentCredits`, dataSend)
      .catch((err) => {
        const message =
          err?.response?.data?.message ||
          "Unable to submit the credits purchase request.";
        notification.error({ message });
        return null;
      });

    setIsSubmitting(false);

    if (response?.data) {
      const feeNote =
        response.data.ccFeeAmount > 0
          ? ` The total invoice, including the $${Number(
              response.data.ccFeeAmount
            ).toFixed(2)} card processing fee, is $${Number(
              response.data.totalAmountDue
            ).toFixed(2)}.`
          : "";
      notification.success({
        message:
          "An invoice for this amount has been generated. Pay it to add these credits to your account - your balance updates once our team confirms the payment." +
          feeNote,
      });
      setAmount(null);
      setPaymentMethod("CHECK");
      onClose();
      if (onRequested) onRequested();
    }
  };

  return (
    <ModalAntd
      title="Buy PARC 360 Credits"
      open={open}
      onCancel={closeModal}
      width={420}
      footer={[
        <Button key="cancel" onClick={closeModal} disabled={isSubmitting}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={submitRequest}
          loading={isSubmitting}
        >
          Request Purchase
        </Button>,
      ]}
      destroyOnClose
    >
      <div className="buy-credits-modal__summary">
        <span>Site</span>
        <strong>{account?.accountName || account?.companyName || "-"}</strong>
      </div>
      <p className="buy-credits-modal__hint">
        Enter how much you'd like to add to your PARC 360 credits balance.
        We'll generate an invoice for this amount - once you pay it, our team
        confirms the payment and posts the credits to your account.
      </p>
      <InputNumber
        className="buy-credits-modal__amount"
        prefix="$"
        min={1}
        step={50}
        value={amount}
        onChange={setAmount}
        placeholder="Amount to purchase"
        disabled={isSubmitting}
      />

      <div className="buy-credits-modal__payment-method">
        <span>How will you pay this invoice?</span>
        <Radio.Group
          value={paymentMethod}
          onChange={(e) => setPaymentMethod(e.target.value)}
          disabled={isSubmitting}
        >
          <Radio value="CHECK">Check / bank transfer</Radio>
          <Radio value="CARD">Credit card</Radio>
        </Radio.Group>
      </div>

      {paymentMethod === "CARD" && cardFeePercent > 0 && (
        <p className="buy-credits-modal__fee-notice">
          A {cardFeePercent}% credit card processing fee applies
          {amount > 0 ? ` ($${cardFeeAmount.toFixed(2)} on this amount)` : ""}. It
          will be added to the invoice total - you'll get a secure payment link
          for the full amount once the invoice is ready.
        </p>
      )}
    </ModalAntd>
  );
}
